import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, CATEGORY_LABELS } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { ComingSoon } from "../components/ComingSoon";
import { ArrowRight, Heart, ShieldCheck, Users } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1607310073276-9f48dec47340?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwdHJhZGluZyUyMGNhcmRzJTIwc3BvcnRzfGVufDB8fHx8MTc4MTg1MTU1OXww&ixlib=rb-4.1.0&q=85";

const CATEGORY_CARDS = [
  { key: "pokemon", img: "https://images.pexels.com/photos/8811594/pexels-photo-8811594.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", span: "md:col-span-2 md:row-span-2" },
  { key: "sports", img: "https://images.unsplash.com/photo-1599873265732-4511510f0908?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHx2aW50YWdlJTIwdHJhZGluZyUyMGNhcmRzJTIwc3BvcnRzfGVufDB8fHx8MTc4MTg1MTU1OXww&ixlib=rb-4.1.0&q=85", span: "" },
  { key: "graded", img: "https://images.unsplash.com/photo-1551306667-f32e7af055f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxncmFkZWQlMjB0cmFkaW5nJTIwY2FyZHMlMjBkaXNwbGF5fGVufDB8fHx8MTc4MTg1MTU2N3ww&ixlib=rb-4.1.0&q=85", span: "" },
  { key: "sealed", img: "https://images.unsplash.com/photo-1577705998148-6da4f3963bc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHx0cmFkaW5nJTIwY2FyZCUyMGJveGVzJTIwc2VhbGVkfGVufDB8fHx8MTc4MTg1MTU1NjN8MA&ixlib=rb-4.1.0&q=85", span: "md:col-span-2" },
];

const MARQUEE = ["POKÉMON", "SPORTS CARDS", "GRADED SLABS", "SEALED ETBs", "SINGLES", "RAW ROOKIES", "PSA · BGS", "FAMILY OWNED"];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#262626]">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#262626] via-[#262626]/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40">
          <div className="max-w-2xl fade-up">
            <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#D32F2F]">For all your hobbyist needs</span>
            <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-[#F9F6F0] leading-none">
              Hometown Era Card Shop<span className="text-[#D32F2F]">.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#F9F6F0]/80 leading-relaxed max-w-xl">
              Where nostalgia meets the next generation of collectors.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/products" data-testid="hero-shop-button" className="inline-flex items-center gap-2 bg-[#D32F2F] text-white font-bold px-7 py-3.5 rounded-md hover:bg-[#B71C1C] transition-all hover:-translate-y-1 shadow-lg">
                Shop the Collection <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/about" data-testid="hero-about-button" className="inline-flex items-center gap-2 border-2 border-[#F9F6F0]/40 text-[#F9F6F0] font-bold px-7 py-3.5 rounded-md hover:bg-[#F9F6F0] hover:text-[#262626] transition-colors">
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="bg-[#262626] text-[#F9F6F0] py-4 overflow-hidden border-y border-white/10">
        <div className="marquee-track">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="text-sm font-bold uppercase tracking-widest px-8 whitespace-nowrap flex items-center">
              {m} <span className="text-[#D32F2F] ml-8">●</span>
            </span>
          ))}
        </div>
      </div>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#D32F2F]">Browse</span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#262626]">Shop by Category</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 md:auto-rows-[200px] gap-5">
          {CATEGORY_CARDS.map((c) => (
            <Link
              key={c.key}
              to={`/products?category=${c.key}`}
              data-testid={`category-link-${c.key}`}
              className={`group relative rounded-lg overflow-hidden border border-[#262626]/10 ${c.span} min-h-[200px]`}
            >
              <img src={c.img} alt={CATEGORY_LABELS[c.key]} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#262626]/90 via-[#262626]/30 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <h3 className="font-display font-black text-2xl text-white">{CATEGORY_LABELS[c.key]}</h3>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#F9F6F0]/80 mt-1 group-hover:text-[#D32F2F] transition-colors">
                  Shop now <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Coming Soon */}
      <ComingSoon variant="dark" />

      {/* Featured */}
      {featured.length > 0 && (
        <section className="bg-white border-y border-[#262626]/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#D32F2F]">Hand-picked</span>
                <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#262626]">Featured Cards</h2>
              </div>
              <Link to="/products" className="hidden sm:inline-flex items-center gap-1 font-semibold text-[#D32F2F] hover:gap-2 transition-all">
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {featured.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Values */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { icon: Heart, title: "Family-Owned", text: "Built by a dad and his kids who love the hobby as much as you do." },
          { icon: ShieldCheck, title: "Trusted & Authentic", text: "Graded slabs, sealed product, and singles you can buy with confidence." },
          { icon: Users, title: "Community First", text: "A welcoming place for collectors of all ages to buy, sell, and trade." },
        ].map((v, i) => (
          <div key={i} className="bg-white rounded-lg border border-[#262626]/10 p-8">
            <div className="w-12 h-12 rounded-full bg-[#D32F2F]/10 flex items-center justify-center mb-5">
              <v.icon className="w-6 h-6 text-[#D32F2F]" />
            </div>
            <h3 className="font-display font-bold text-xl text-[#262626]">{v.title}</h3>
            <p className="mt-2 text-[#262626]/70 leading-relaxed">{v.text}</p>
          </div>
        ))}
      </section>

      {/* Story teaser */}
      <section className="bg-[#262626] text-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <img src="https://customer-assets.emergentagent.com/job_hometown-era/artifacts/j1pansnl_A8FC09D6-4628-46AE-9796-074749941CB3.png" alt="Our family" className="rounded-lg w-full h-80 object-cover object-top" />
          <div>
            <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#D32F2F]">Our Story</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">More than a business — it's family.</h2>
            <p className="mt-5 text-[#F9F6F0]/80 leading-relaxed">
              What started as a lifelong passion has grown into a dream come true. As a dad of two boys who love
              collecting as much as I do — and a baby girl on the way — we built Hometown Era Cards to share the
              hobby across generations.
            </p>
            <Link to="/about" className="inline-flex items-center gap-2 mt-7 bg-[#D32F2F] text-white font-bold px-7 py-3.5 rounded-md hover:bg-[#B71C1C] transition-colors">
              Read our story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
